import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, AlertCircle, CheckCircle2 } from 'lucide-react';

const DispatchForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | sent | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Unable to process request. Please try again.');
      }
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#004C97] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">MoveraCare Mobility</h1>
          <p className="text-[#F9A825] text-lg font-semibold">Password Recovery</p>
        </div>

        <Card className="border-2 border-[#F9A825]/20 shadow-2xl" data-testid="dispatch-forgot-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center text-[#004C97]">Reset your password</CardTitle>
            <CardDescription className="text-center">
              Enter the email address associated with your dispatch account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status === 'sent' ? (
              <div
                className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start space-x-3"
                data-testid="dispatch-forgot-success"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-emerald-900 font-medium">
                    If an account exists for that email, we've sent a password-reset link.
                  </p>
                  <p className="text-xs text-emerald-800 mt-1">
                    The link is valid for 30 minutes and can be used only once. Check your inbox and spam folder.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="dispatch-forgot-form">
                {status === 'error' && (
                  <div
                    className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2"
                    data-testid="dispatch-forgot-error"
                  >
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{errorMsg}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700" htmlFor="dispatch-forgot-email">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="dispatch-forgot-email"
                      data-testid="dispatch-forgot-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="dispatch-forgot-submit"
                  className="w-full bg-[#F9A825] hover:bg-[#E08E1E] text-white font-semibold py-2"
                >
                  {status === 'loading' ? 'Sending link…' : 'Send reset link'}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200 flex justify-center">
              <Link
                to="/dispatch/login"
                data-testid="dispatch-forgot-back-to-login"
                className="text-sm text-[#004C97] hover:underline font-medium"
              >
                Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DispatchForgotPassword;
